/**
 * Entity Base - Core entity class with component system
 * 
 * Features:
 * - Component-based architecture
 * - Transform management
 * - Event system
 * - Lifecycle management
 */

import { Scene } from "@babylonjs/core/scene";
import { Vector3, Quaternion } from "@babylonjs/core/Maths/math.vector";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { AnimationGroup } from "@babylonjs/core/Animations/animationGroup";
import { Skeleton } from "@babylonjs/core/Bones/skeleton";
import { PhysicsAggregate } from "@babylonjs/core/Physics/v2/physicsAggregate";
import { PhysicsShapeType } from "@babylonjs/core/Physics/v2/IPhysicsEnginePlugin";

import { IDisposable } from "../core/Engine.types";
import { LoadedModelAsset } from "../assets/Asset.types";
import {
    EntityState,
    ComponentType,
    IComponent,
    IEntity,
    EntityConfig,
    EntityEventType,
    EntityEventHandler,
    EntityEvent,
    ColliderShape,
} from "./Entity.types";

/**
 * Generate unique ID
 */
function generateId(): string {
    return `entity_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Map collider shape to physics shape type
 */
function mapColliderToPhysics(shape: ColliderShape): PhysicsShapeType {
    switch (shape) {
        case ColliderShape.BOX: return PhysicsShapeType.BOX;
        case ColliderShape.SPHERE: return PhysicsShapeType.SPHERE;
        case ColliderShape.CAPSULE: return PhysicsShapeType.CAPSULE;
        case ColliderShape.CYLINDER: return PhysicsShapeType.CYLINDER;
        case ColliderShape.MESH: return PhysicsShapeType.MESH;
        case ColliderShape.CONVEX: return PhysicsShapeType.CONVEX_HULL;
        default: return PhysicsShapeType.BOX;
    }
}

/**
 * Entity - Base game entity with component system
 */
export class Entity implements IEntity, IDisposable {
    private _id: string;
    private _name: string;
    private _state: EntityState = EntityState.INACTIVE;
    private _scene: Scene;
    private _tags: Set<string> = new Set();
    
    // Components
    private _components: Map<ComponentType, IComponent> = new Map();
    private _transformNode: TransformNode;
    private _meshes: AbstractMesh[] = [];
    private _animationGroups: AnimationGroup[] = [];
    private _skeleton: Skeleton | null = null;
    private _physicsAggregate: PhysicsAggregate | null = null;
    
    // Event handlers
    private _eventHandlers: Map<EntityEventType, Set<EntityEventHandler>> = new Map();

    public get id(): string { return this._id; }
    public get name(): string { return this._name; }
    public get scene(): Scene { return this._scene; }
    public get tags(): Set<string> { return this._tags; }
    public get transformNode(): TransformNode { return this._transformNode; }
    public get meshes(): AbstractMesh[] { return this._meshes; }
    public get skeleton(): Skeleton | null { return this._skeleton; }

    public get state(): EntityState { return this._state; }
    public set state(value: EntityState) { 
        this._state = value;
        this._onStateChange(value);
    }

    // Transform shortcuts
    public get position(): Vector3 {
        return this._transformNode.position;
    }
    public set position(value: Vector3) {
        this._transformNode.position = value;
        // Sync physics body position if kinematic
        this._syncPhysicsTransform();
    }

    public get rotation(): Quaternion {
        return this._transformNode.rotationQuaternion || Quaternion.Identity();
    }
    public set rotation(value: Quaternion) {
        this._transformNode.rotationQuaternion = value;
        // Sync physics body rotation if kinematic
        this._syncPhysicsTransform();
    }

    public get scale(): Vector3 {
        return this._transformNode.scaling;
    }
    public set scale(value: Vector3) {
        this._transformNode.scaling = value;
    }

    public get forward(): Vector3 {
        return this._transformNode.forward;
    }

    constructor(scene: Scene, config: EntityConfig) {
        this._id = config.id || generateId();
        this._name = config.name;
        this._scene = scene;

        // Create transform node
        this._transformNode = new TransformNode(this._name, scene);
        this._transformNode.rotationQuaternion = Quaternion.Identity();

        // Apply initial transform
        if (config.position) {
            this._transformNode.position = config.position;
        }
        if (config.rotation) {
            this._transformNode.rotationQuaternion = config.rotation;
        }
        if (config.scale) {
            this._transformNode.scaling = config.scale;
        }

        // Apply tags
        if (config.tags) {
            config.tags.forEach(tag => this._tags.add(tag));
        }

        // Store config for later initialization
        (this as any)._config = config;
    }

    /**
     * Initialize entity with loaded model
     */
    public async initialize(): Promise<void> {
        // Initialize all components
        for (const [, component] of this._components) {
            if (component.initialize) {
                await component.initialize();
            }
        }

        this._state = EntityState.ACTIVE;
        this.emit("spawn");
    }

    /**
     * Attach a loaded model to this entity
     */
    public attachModel(modelAsset: LoadedModelAsset): void {
        // Parent model root to entity transform
        modelAsset.rootNode.parent = this._transformNode;
        modelAsset.rootNode.position = Vector3.Zero();
        modelAsset.rootNode.rotationQuaternion = Quaternion.Identity();

        // Store references
        this._meshes = modelAsset.meshes;
        this._animationGroups = modelAsset.animationGroups;
        this._skeleton = modelAsset.skeleton;

        // Enable all meshes
        this._meshes.forEach(mesh => {
            mesh.setEnabled(true);
        });

        console.log(`Entity "${this._name}": Model attached with ${this._meshes.length} meshes`);
    }

    /**
     * Setup physics on this entity
     * 
     * For NPCs/static objects: use isKinematic=true (mass=0)
     * For dynamic objects: use appropriate mass
     * 
     * @param shape - Collision shape type
     * @param mass - Mass for dynamic bodies (ignored if isKinematic)
     * @param options - Physics options
     */
    public setupPhysics(
        shape: ColliderShape = ColliderShape.BOX,
        mass: number = 1,
        options?: {
            friction?: number;
            restitution?: number;
            isKinematic?: boolean;
        }
    ): PhysicsAggregate | null {
        if (this._meshes.length === 0) {
            console.warn(`Entity "${this._name}": Cannot setup physics - no meshes attached`);
            return null;
        }

        // Find the main mesh (usually the first non-root mesh with geometry)
        const mainMesh = this._meshes.find(m => m.getTotalVertices() > 0);
        if (!mainMesh) {
            console.warn(`Entity "${this._name}": Cannot setup physics - no mesh with geometry found`);
            return null;
        }

        // Ensure world matrix is computed before creating physics
        mainMesh.computeWorldMatrix(true);

        const physicsShape = mapColliderToPhysics(shape);
        const isKinematic = options?.isKinematic ?? false;
        const finalMass = isKinematic ? 0 : mass;
        
        this._physicsAggregate = new PhysicsAggregate(
            mainMesh,
            physicsShape,
            {
                mass: finalMass,
                friction: options?.friction ?? 0.5,
                restitution: options?.restitution ?? 0.2,
            },
            this._scene
        );

        // For kinematic bodies, we need to mark them as such
        if (isKinematic && this._physicsAggregate.body) {
            // Kinematic bodies have mass 0 but can still collide
            // They're moved by code, not by physics forces
            this._physicsAggregate.body.setMotionType(1); // 1 = KINEMATIC in Havok
        }

        console.log(`Entity "${this._name}": Physics setup with shape ${shape}, mass ${finalMass}, kinematic=${isKinematic}`);
        return this._physicsAggregate;
    }

    /**
     * Sync physics body transform with entity transform
     * Call this after moving kinematic entities
     */
    private _syncPhysicsTransform(): void {
        if (!this._physicsAggregate?.body) return;
        
        // For kinematic bodies, we need to set the transform on the physics body
        // The mesh follows the physics body, not the transform node
        const body = this._physicsAggregate.body;
        
        // Get the world position of the transform node
        const worldPos = this._transformNode.getAbsolutePosition();
        const worldRot = this._transformNode.absoluteRotationQuaternion || Quaternion.Identity();
        
        // Set physics body transform - this makes the mesh follow
        body.setTargetTransform(worldPos, worldRot);
    }

    /**
     * Force sync physics transform (public method for manual sync)
     */
    public syncPhysics(): void {
        this._syncPhysicsTransform();
    }

    /**
     * Check if entity has physics enabled
     */
    public get hasPhysics(): boolean {
        return this._physicsAggregate !== null;
    }

    /**
     * Get the physics aggregate (for advanced physics operations)
     */
    public get physicsAggregate(): PhysicsAggregate | null {
        return this._physicsAggregate;
    }

    /**
     * Update entity (called every frame)
     */
    public update(deltaTime: number): void {
        if (this._state !== EntityState.ACTIVE) return;

        // Update all enabled components
        for (const [, component] of this._components) {
            if (component.enabled && component.update) {
                component.update(deltaTime);
            }
        }
    }

    /**
     * Get component by type
     */
    public getComponent<T extends IComponent>(type: ComponentType): T | null {
        return (this._components.get(type) as T) || null;
    }

    /**
     * Check if entity has component
     */
    public hasComponent(type: ComponentType): boolean {
        return this._components.has(type);
    }

    /**
     * Add a component to this entity
     */
    public addComponent(component: IComponent): void {
        this._components.set(component.type, component);
    }

    // Animation helpers
    /**
     * Play an animation by name
     */
    public playAnimation(
        name: string, 
        loop: boolean = true, 
        speed: number = 1.0,
        from?: number,
        to?: number
    ): AnimationGroup | null {
        const animGroup = this._animationGroups.find(ag => 
            ag.name.toLowerCase().includes(name.toLowerCase())
        );
        
        if (!animGroup) {
            console.warn(`Entity "${this._name}": Animation "${name}" not found`);
            console.log("Available animations:", this._animationGroups.map(ag => ag.name));
            return null;
        }

        // Stop other animations
        this._animationGroups.forEach(ag => {
            if (ag !== animGroup) ag.stop();
        });

        animGroup.speedRatio = speed;
        
        if (from !== undefined && to !== undefined) {
            animGroup.start(loop, speed, from, to);
        } else {
            animGroup.start(loop, speed);
        }

        return animGroup;
    }

    /**
     * Stop all animations
     */
    public stopAnimations(): void {
        this._animationGroups.forEach(ag => ag.stop());
    }

    /**
     * Get animation names
     */
    public getAnimationNames(): string[] {
        return this._animationGroups.map(ag => ag.name);
    }

    // Texture/Material helpers
    /**
     * Analyze textures on this entity's meshes
     * Use this to debug texture issues with GLB models
     */
    public analyzeTextures(): void {
        // Dynamic import to avoid circular dependency
        import("../assets/TextureUtils").then(({ TextureUtils }) => {
            console.log(`\n=== Texture Analysis for "${this._name}" ===`);
            TextureUtils.printTextureAnalysis(this._meshes);
        });
    }

    /**
     * Apply textures to this entity's materials
     * @param config Texture configuration
     * @param basePath Base path for texture files (default: "assets/")
     */
    public applyTextures(
        config: {
            diffuse?: string;
            normal?: string;
            metallicRoughness?: string;
            occlusion?: string;
            emissive?: string;
            materialNames?: string[];
        },
        basePath: string = "assets/"
    ): void {
        import("../assets/TextureUtils").then(({ TextureUtils }) => {
            TextureUtils.applyTextures(this._meshes, this._scene, config, basePath);
        });
    }

    /**
     * Get all materials used by this entity
     */
    public getMaterials(): import("@babylonjs/core/Materials/material").Material[] {
        const materials = new Set<import("@babylonjs/core/Materials/material").Material>();
        this._meshes.forEach(mesh => {
            if (mesh.material) {
                materials.add(mesh.material);
            }
        });
        return Array.from(materials);
    }

    // Physics helpers
    /**
     * Apply force to entity
     */
    public applyForce(force: Vector3, point?: Vector3): void {
        if (!this._physicsAggregate?.body) return;
        
        if (point) {
            this._physicsAggregate.body.applyForce(force, point);
        } else {
            this._physicsAggregate.body.applyForce(force, this.position);
        }
    }

    /**
     * Apply impulse to entity
     */
    public applyImpulse(impulse: Vector3, point?: Vector3): void {
        if (!this._physicsAggregate?.body) return;
        
        if (point) {
            this._physicsAggregate.body.applyImpulse(impulse, point);
        } else {
            this._physicsAggregate.body.applyImpulse(impulse, this.position);
        }
    }

    /**
     * Set linear velocity
     */
    public setVelocity(velocity: Vector3): void {
        if (!this._physicsAggregate?.body) return;
        this._physicsAggregate.body.setLinearVelocity(velocity);
    }

    /**
     * Get linear velocity
     */
    public getVelocity(): Vector3 {
        if (!this._physicsAggregate?.body) return Vector3.Zero();
        return this._physicsAggregate.body.getLinearVelocity();
    }

    // Event system
    /**
     * Subscribe to entity events
     */
    public on(event: EntityEventType, handler: EntityEventHandler): void {
        if (!this._eventHandlers.has(event)) {
            this._eventHandlers.set(event, new Set());
        }
        this._eventHandlers.get(event)!.add(handler);
    }

    /**
     * Unsubscribe from entity events
     */
    public off(event: EntityEventType, handler: EntityEventHandler): void {
        this._eventHandlers.get(event)?.delete(handler);
    }

    /**
     * Emit entity event
     */
    public emit(event: EntityEventType, data?: unknown): void {
        const handlers = this._eventHandlers.get(event);
        if (!handlers) return;

        const entityEvent: EntityEvent = {
            type: event,
            entity: this,
            data,
            timestamp: performance.now(),
        };

        handlers.forEach(handler => {
            try {
                handler(entityEvent);
            } catch (error) {
                console.error(`Entity "${this._name}": Error in event handler for "${event}"`, error);
            }
        });
    }

    // Visibility
    /**
     * Set entity visibility
     */
    public setVisible(visible: boolean): void {
        this._meshes.forEach(mesh => mesh.setEnabled(visible));
    }

    /**
     * Check if entity is visible
     */
    public isVisible(): boolean {
        return this._meshes.length > 0 && this._meshes[0].isEnabled();
    }

    /**
     * Get the entity's world bounding box
     * Combines all mesh bounding boxes
     */
    public getBoundingBox(): { min: Vector3; max: Vector3; size: Vector3; center: Vector3 } {
        if (this._meshes.length === 0) {
            return {
                min: this.position.clone(),
                max: this.position.clone(),
                size: Vector3.Zero(),
                center: this.position.clone(),
            };
        }

        let minX = Infinity, minY = Infinity, minZ = Infinity;
        let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
        let hasValidBounds = false;

        for (const mesh of this._meshes) {
            // Force world matrix computation
            mesh.computeWorldMatrix(true);
            
            if (!mesh.getBoundingInfo()) continue;
            
            // Refresh bounding info after world matrix update
            mesh.refreshBoundingInfo({ applySkeleton: true });
            
            const boundingBox = mesh.getBoundingInfo().boundingBox;
            const worldMin = boundingBox.minimumWorld;
            const worldMax = boundingBox.maximumWorld;

            // Skip meshes with no geometry
            if (mesh.getTotalVertices() === 0) continue;

            minX = Math.min(minX, worldMin.x);
            minY = Math.min(minY, worldMin.y);
            minZ = Math.min(minZ, worldMin.z);
            maxX = Math.max(maxX, worldMax.x);
            maxY = Math.max(maxY, worldMax.y);
            maxZ = Math.max(maxZ, worldMax.z);
            hasValidBounds = true;
        }

        // Fallback if no valid bounds found
        if (!hasValidBounds) {
            return {
                min: this.position.clone(),
                max: this.position.clone(),
                size: Vector3.Zero(),
                center: this.position.clone(),
            };
        }

        const min = new Vector3(minX, minY, minZ);
        const max = new Vector3(maxX, maxY, maxZ);
        const size = max.subtract(min);
        const center = min.add(size.scale(0.5));

        return { min, max, size, center };
    }

    /**
     * Get entity's height (Y dimension from bounding box)
     */
    public getHeight(): number {
        const bbox = this.getBoundingBox();
        return bbox.size.y;
    }

    /**
     * Get entity's bottom Y position (lowest point)
     */
    public getBottomY(): number {
        const bbox = this.getBoundingBox();
        return bbox.min.y;
    }

    /**
     * Set Y position so entity's bottom is at targetY
     * Useful for placing on terrain
     */
    public setBottomY(targetY: number): void {
        const bottomY = this.getBottomY();
        const offset = this.position.y - bottomY;
        this.position.y = targetY + offset;
    }

    /**
     * Handle state changes
     */
    private _onStateChange(newState: EntityState): void {
        switch (newState) {
            case EntityState.ACTIVE:
                this.emit("enable");
                break;
            case EntityState.INACTIVE:
            case EntityState.PAUSED:
                this.emit("disable");
                break;
            case EntityState.DESTROYED:
                this.emit("destroy");
                break;
        }
    }

    /**
     * Dispose entity and all resources
     */
    public dispose(): void {
        this._state = EntityState.DESTROYED;

        // Dispose components
        for (const [, component] of this._components) {
            if (component.dispose) {
                component.dispose();
            }
        }
        this._components.clear();

        // Dispose physics
        if (this._physicsAggregate) {
            this._physicsAggregate.dispose();
            this._physicsAggregate = null;
        }

        // Dispose animations
        this._animationGroups.forEach(ag => ag.dispose());
        this._animationGroups = [];

        // Dispose skeleton
        if (this._skeleton) {
            this._skeleton.dispose();
            this._skeleton = null;
        }

        // Dispose meshes
        this._meshes.forEach(mesh => mesh.dispose());
        this._meshes = [];

        // Dispose transform
        this._transformNode.dispose();

        // Clear events
        this._eventHandlers.clear();

        console.log(`Entity "${this._name}": Disposed`);
    }
}
